import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarDays, List, Loader2, LogOut, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import AdminCalendar from "@/components/AdminCalendar";

const STATUSES = ["Pending", "Dispatched", "In-Progress", "Completed", "Cancelled"];

const STATUS_STYLES = {
    Pending: "border-amber-500/40 bg-amber-500/10 text-amber-400",
    Dispatched: "border-cyan-500/40 bg-cyan-500/10 text-cyan-400",
    "In-Progress": "border-blue-500/40 bg-blue-500/10 text-blue-400",
    Completed: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
    Cancelled: "border-red-500/40 bg-red-500/10 text-red-400",
};

function DetailRow({ label, value }) {
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">{label}</div>
            <div className="text-sm text-zinc-200 mt-1 break-words">{value || "—"}</div>
        </div>
    );
}

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [bookings, setBookings] = useState(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [selected, setSelected] = useState(null);
    const [view, setView] = useState("list");

    const load = async () => {
        try {
            const { data } = await api.get("/bookings");
            setBookings(data);
        } catch (err) {
            if (err?.response?.status === 401 || err?.response?.status === 403) {
                localStorage.removeItem("masons_token");
                navigate("/admin");
            }
        }
    };

    useEffect(() => {
        if (!localStorage.getItem("masons_token")) {
            navigate("/admin");
            return;
        }
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateStatus = async (id, status) => {
        const { data } = await api.patch(`/bookings/${id}/status`, { status });
        setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status: data.status } : b)));
        setSelected((prev) => (prev && prev.id === id ? { ...prev, status: data.status } : prev));
    };

    const filtered = useMemo(() => {
        if (!bookings) return [];
        const q = search.toLowerCase();
        return bookings.filter(
            (b) =>
                (!statusFilter || b.status === statusFilter) &&
                (!q ||
                    b.customer_name.toLowerCase().includes(q) ||
                    b.phone.includes(q) ||
                    b.vehicle.toLowerCase().includes(q) ||
                    b.id.toLowerCase().includes(q))
        );
    }, [bookings, search, statusFilter]);

    const kpis = useMemo(() => {
        if (!bookings) return [];
        return [
            { label: "Total Requests", value: bookings.length },
            { label: "Pending", value: bookings.filter((b) => b.status === "Pending").length },
            { label: "Dispatched", value: bookings.filter((b) => b.status === "Dispatched").length },
            { label: "Completed", value: bookings.filter((b) => b.status === "Completed").length },
        ];
    }, [bookings]);

    const logout = () => {
        localStorage.removeItem("masons_token");
        navigate("/admin");
    };

    return (
        <div className="min-h-screen bg-[#060709] text-white" data-testid="admin-dashboard">
            <header className="sticky top-0 z-40 backdrop-blur-2xl bg-[#0B0C0E]/85 border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Logo />
                        <span className="font-display font-extrabold uppercase">Dispatch Console</span>
                    </div>
                    <button
                        onClick={logout}
                        data-testid="admin-logout-button"
                        className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-zinc-400 hover:text-red-400 transition-colors duration-200"
                    >
                        <LogOut className="w-4 h-4" /> Logout
                    </button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-10">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-kpi-cards">
                    {kpis.map((kpi, i) => (
                        <motion.div
                            key={kpi.label}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: i * 0.07 }}
                            data-testid={`admin-kpi-${kpi.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="rounded-2xl border border-[#22262F] bg-[#121418] p-6"
                        >
                            <div className="font-display text-4xl font-bold text-white">{kpi.value}</div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mt-1">
                                {kpi.label}
                            </div>
                        </motion.div>
                    ))}
                </div>

                <div className="flex flex-wrap gap-4 mb-6 items-center">
                    <div className="relative flex-1 min-w-[240px]">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search name, phone, vehicle or ref..."
                            data-testid="admin-search-input"
                            className="w-full bg-[#0B0C0E] border border-[#383E4C] rounded-xl pl-11 pr-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-amber-500 transition-colors duration-200"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        data-testid="admin-status-filter"
                        className="bg-[#0B0C0E] border border-[#383E4C] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-amber-500 transition-colors duration-200"
                    >
                        <option value="">All statuses</option>
                        {STATUSES.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                    <div className="flex rounded-xl border border-[#383E4C] overflow-hidden" data-testid="admin-view-toggle">
                        <button
                            onClick={() => setView("list")}
                            data-testid="admin-view-list-button"
                            className={`flex items-center gap-2 px-4 py-3 text-xs font-mono uppercase tracking-[0.15em] transition-colors duration-200 ${
                                view === "list" ? "bg-amber-500 text-[#0B0C0E]" : "text-zinc-400 hover:text-white"
                            }`}
                        >
                            <List className="w-4 h-4" /> List
                        </button>
                        <button
                            onClick={() => setView("calendar")}
                            data-testid="admin-view-calendar-button"
                            className={`flex items-center gap-2 px-4 py-3 text-xs font-mono uppercase tracking-[0.15em] transition-colors duration-200 ${
                                view === "calendar" ? "bg-amber-500 text-[#0B0C0E]" : "text-zinc-400 hover:text-white"
                            }`}
                        >
                            <CalendarDays className="w-4 h-4" /> Calendar
                        </button>
                    </div>
                </div>

                {!bookings ? (
                    <div className="flex justify-center py-20" data-testid="admin-loading">
                        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                    </div>
                ) : view === "calendar" ? (
                    <AdminCalendar bookings={filtered} statusStyles={STATUS_STYLES} onSelect={setSelected} />
                ) : filtered.length === 0 ? (
                    <div className="text-center py-20 text-zinc-500 font-mono text-sm uppercase tracking-widest" data-testid="admin-empty-state">
                        No bookings match
                    </div>
                ) : (
                    <div className="space-y-4" data-testid="admin-bookings-list">
                        {filtered.map((b) => (
                            <motion.article
                                key={b.id}
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                                onClick={() => setSelected(b)}
                                data-testid={`admin-booking-row-${b.id}`}
                                className="rounded-2xl border border-[#22262F] bg-[#121418] p-6 grid lg:grid-cols-12 gap-4 items-center cursor-pointer hover:border-amber-500/40 transition-colors duration-200"
                            >
                                <div className="lg:col-span-3">
                                    <div className="font-mono text-xs text-amber-400">{b.id}</div>
                                    <div className="font-display text-lg font-bold uppercase text-white mt-1">
                                        {b.customer_name}
                                    </div>
                                    <div className="text-xs text-zinc-500">{b.phone}</div>
                                </div>
                                <div className="lg:col-span-3">
                                    <div className="text-sm text-zinc-300">{b.vehicle}</div>
                                    <div className="text-xs text-zinc-500 mt-1">{b.service}</div>
                                </div>
                                <div className="lg:col-span-3">
                                    <div className="text-xs font-mono text-zinc-400">
                                        {b.date} · {b.time_slot}
                                    </div>
                                    <div className="text-xs text-zinc-500 mt-1">{b.address}</div>
                                </div>
                                <div className="lg:col-span-3 flex items-center gap-3 lg:justify-end">
                                    <span
                                        className={`text-[10px] font-mono uppercase tracking-[0.15em] border rounded-full px-3 py-1 ${STATUS_STYLES[b.status] || ""}`}
                                        data-testid={`admin-booking-status-${b.id}`}
                                    >
                                        {b.status}
                                    </span>
                                    <select
                                        value={b.status}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => updateStatus(b.id, e.target.value)}
                                        data-testid={`admin-status-select-${b.id}`}
                                        className="bg-[#0B0C0E] border border-[#383E4C] rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500 transition-colors duration-200"
                                    >
                                        {STATUSES.map((s) => (
                                            <option key={s} value={s}>
                                                {s}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                )}
            </main>

            <AnimatePresence>
                {selected && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={() => setSelected(null)}
                        data-testid="booking-detail-modal"
                        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 24, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 24, scale: 0.97 }}
                            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-2xl rounded-2xl border border-[#383E4C] bg-[#121418] p-8 sm:p-10 max-h-[85vh] overflow-y-auto"
                        >
                            <div className="flex items-start justify-between gap-4 mb-8">
                                <div>
                                    <div className="font-mono text-xs text-amber-400">{selected.id}</div>
                                    <h3 className="font-display text-3xl font-bold uppercase text-white mt-1">
                                        {selected.customer_name}
                                    </h3>
                                    <span
                                        className={`inline-block mt-3 text-[10px] font-mono uppercase tracking-[0.15em] border rounded-full px-3 py-1 ${STATUS_STYLES[selected.status] || ""}`}
                                        data-testid="booking-detail-status"
                                    >
                                        {selected.status}
                                    </span>
                                </div>
                                <button
                                    onClick={() => setSelected(null)}
                                    data-testid="booking-detail-close-button"
                                    className="text-zinc-500 hover:text-white transition-colors duration-200 p-1"
                                    aria-label="Close details"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-6" data-testid="booking-detail-fields">
                                <DetailRow label="Phone" value={selected.phone} />
                                <DetailRow label="Email" value={selected.email} />
                                <DetailRow label="Vehicle" value={selected.vehicle} />
                                <DetailRow label="Service" value={selected.service} />
                                <DetailRow label="Date" value={selected.date} />
                                <DetailRow label="Time Slot" value={selected.time_slot} />
                                <div className="sm:col-span-2">
                                    <DetailRow label="Service Location" value={selected.address} />
                                </div>
                                <DetailRow label="Add-On" value={selected.addon} />
                                <div className="sm:col-span-2">
                                    <DetailRow label="Notes / Symptoms" value={selected.notes} />
                                </div>
                                <DetailRow
                                    label="Requested At"
                                    value={new Date(selected.created_at).toLocaleString("en-AU")}
                                />
                            </div>
                            <div className="mt-8 pt-6 border-t border-[#22262F] flex flex-wrap items-center gap-3">
                                <a
                                    href={`tel:${selected.phone.replace(/[^+\d]/g, "")}`}
                                    data-testid="booking-detail-call-button"
                                    className="bg-amber-500 text-[#0B0C0E] text-xs font-bold uppercase tracking-widest rounded-full px-5 py-2.5 hover:bg-amber-400 transition-colors duration-200"
                                >
                                    Call Customer
                                </a>
                                <select
                                    value={selected.status}
                                    onChange={(e) => updateStatus(selected.id, e.target.value)}
                                    data-testid="booking-detail-status-select"
                                    className="bg-[#0B0C0E] border border-[#383E4C] rounded-full px-4 py-2.5 text-xs text-white focus:outline-none focus:border-amber-500 transition-colors duration-200"
                                >
                                    {STATUSES.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
